import { memo, useCallback, useRef, useState, type JSX } from 'react'

import { OriginGateway } from '@nativewaves/nw-cloud-sdk/origin'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Table as ReactTableType } from '@tanstack/react-table'
import { VirtualItem } from '@tanstack/react-virtual'
import { pick } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { useColumns, NewDialog, RowActions, useAsyncContentCaching, SuccessPanelActions } from 'domains/OriginGateways'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { originGatewaysOverviewRoute } from 'pages/NWPlatform/OriginGateways'
import { rawRoute } from 'pages/NWPlatform/OriginGateways/Gateway/Routing'
import { gatewayQueryKeys } from 'services/queryKeys'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const navigate = useNavigate({ from: originGatewaysOverviewRoute.fullPath })
  const routeParams = originGatewaysOverviewRoute.useParams()
  const { query } = originGatewaysOverviewRoute.useSearch()

  const tableInstance = useRef<ReactTableType<OriginGateway>>(undefined)
  const virtualizerInstanceRef = useRef<VirtualItem[]>(undefined)

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])

  const { gatewaysQuery, setBatchGetEnabled } = useAsyncContentCaching({
    virtualItems: virtualizerInstanceRef.current,
    setLoadingRows,
  })

  const columns = useColumns()

  const handleSuccess = useCallback(
    (data: OriginGateway) => {
      enqueueSnackbar(`${t('domain:OriginGateway.gatewayCreated', { name: data.name })}`, {
        description: `${t('domain:OriginGateway.gatewayCreatedDescription')}`,
        variant: 'panel',
        persist: true,
        icon: 'success',
        Actions: (props) => <SuccessPanelActions {...props} {...routeParams} gatewayId={data.id} />,
      })
    },
    [enqueueSnackbar, routeParams, t],
  )

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:OriginGateway.searchPlaceholder')}
          onSearch={({ search }) => navigate({ search: (prev) => ({ ...prev, query: search }) })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(() => {
            queryClient.cancelQueries({ queryKey: gatewayQueryKeys.batchGets() })
            setBatchGetEnabled(false)
            queryClient.invalidateQueries({
              queryKey: gatewayQueryKeys.list({ environmentId: routeParams.environmentId, query }).queryKey,
            })
          }, [query, queryClient, routeParams.environmentId, setBatchGetEnabled])}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(instance) => (tableInstance.current = instance)}
        size={48}
        data={gatewaysQuery.data?.flat ?? []}
        columns={columns}
        isInitialLoadingGlobal={gatewaysQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: [] }), {})}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={originGatewaysOverviewRoute.fullPath}
            to={rawRoute.to}
            params={{ gatewayId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        enableInfinity
        infinityProps={pick(gatewaysQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        enableVirtualization
        virtualizedProps={{
          getVirtualItems: (instance) => (virtualizerInstanceRef.current = instance),
        }}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('entity:origin.gateway', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
        tableOptions={{
          getRowId: (audio) => audio.id,
        }}
      />
      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog onSuccess={handleSuccess} />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
